import { ref, computed, watch, toValue, nextTick, onScopeDispose, readonly } from "vue";
import { useElementBounding, useEventListener, usePreferredReducedMotion, useWindowSize } from "@vueuse/core";
// Distance the pointer must travel before a pointerdown is treated as a drag
// rather than a click — small enough to feel responsive, large enough that a
// jittery click on the VideoJS surface doesn't toggle play/pause and reposition.
const DRAG_THRESHOLD_PX = 4;
// Elements inside the PiP container that should NOT initiate a drag.
const DRAG_IGNORE_SELECTOR = "button, a, .vjs-control-bar, .vjs-big-play-button, .vjs-menu, .vjs-modal-dialog";
// Clearance kept between the player's inline-end edge and the side panel.
const PIP_PANEL_GAP_PX = 16;
// Below this viewport width the PiP is top-anchored and the panel is ~full-width, so a horizontal shift makes no sense.
const PIP_COLLISION_MIN_VIEWPORT_PX = 790;
// Resting inline-end inset of the PiP; mirrors `inset-inline-end: max(rem(16), …)` in MediaPlayer.vue.
const PIP_BASE_INSET_PX = 16;
// Polite live-region cue lingers this long before clearing.
const PIP_EXIT_ANNOUNCEMENT_MS = 3000;
// Window during which a click on the PiP surface is treated as the tail of a drag (not a play/pause toggle).
const POST_DRAG_CLICK_GATE_MS = 300;
// SSR-safe RTL check; reads the legacy app's layout direction.
const isRtl = () => typeof window !== "undefined" && !!window.Ramius?.is_rtl;
// Two ticks: drain pre-render microtasks, then let Vue's render flush (adds --pip) commit before measuring.
const afterRenderFlush = (callback) => void nextTick(() => nextTick(callback));
/** Scroll/play state machine: activates PiP when playback begins while scrolled out, closes on scroll-back-in or dismiss. */
const usePipVisibility = ({ targetRef, isPlaying, threshold, enabled }) => {
    const isScrolledOut = ref(false);
    const isDismissed = ref(false);
    // Stateful so PiP persists through pauses; activates on play-while-scrolled-out, closes on scroll-back-in or dismiss.
    const isPip = ref(false);
    let observer = null;
    const handleIntersect = (entries) => {
        for (const entry of entries) {
            const wasScrolledOut = isScrolledOut.value;
            isScrolledOut.value = entry.intersectionRatio < threshold && entry.boundingClientRect.top < 0;
            if (wasScrolledOut && !isScrolledOut.value) {
                isDismissed.value = false;
            }
        }
    };
    const cleanupObserver = () => {
        if (observer) {
            observer.disconnect();
            observer = null;
        }
    };
    // Sync flush is safe only because no isPip watcher (e.g. the FLIP watcher) writes back to
    // isScrolledOut/isDismissed/isPlaying — preserve that invariant.
    // Activate PiP when play begins while scrolled out (also clears any prior dismiss).
    watch([isPlaying, isScrolledOut], ([playing, scrolledOut]) => {
        if (playing && scrolledOut) {
            isDismissed.value = false;
            isPip.value = true;
        }
    }, { flush: "sync" });
    watch(isScrolledOut, (scrolledOut) => {
        if (!scrolledOut)
            isPip.value = false;
    }, { flush: "sync" });
    watch(isDismissed, (dismissed) => {
        if (dismissed)
            isPip.value = false;
    }, { flush: "sync" });
    watch(() => {
        const isEnabled = enabled !== undefined ? toValue(enabled) : true;
        return isEnabled ? toValue(targetRef) : null;
    }, (el) => {
        cleanupObserver();
        if (!el) {
            isScrolledOut.value = false;
            return;
        }
        observer = new IntersectionObserver(handleIntersect, { threshold });
        observer.observe(el);
    }, { immediate: true });
    const dismiss = () => {
        isDismissed.value = true;
    };
    onScopeDispose(cleanupObserver);
    return { isPip, isScrolledOut, dismiss };
};
/** Polite live-region cue, shown briefly when the player returns from mini-player to inline. */
const usePipAnnouncement = (isPip) => {
    const showPipExitAnnouncement = ref(false);
    let timer = null;
    const clearTimer = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };
    watch(isPip, (active, previous) => {
        if (previous && !active) {
            showPipExitAnnouncement.value = true;
            clearTimer();
            timer = setTimeout(() => {
                showPipExitAnnouncement.value = false;
                timer = null;
            }, PIP_EXIT_ANNOUNCEMENT_MS);
        }
    });
    onScopeDispose(clearTimer);
    return { showPipExitAnnouncement };
};
/**
 * FLIP transition between inline and floating player via the Web Animations API (honours
 * prefers-reduced-motion). Also exposes the inline placeholder sizing captured at PiP entry.
 */
const usePipFlip = ({ resolveTargetEl, isPip, isScrolledOut, pipOffset }) => {
    // Web Animations API runs outside the CSS `prefers-reduced-motion` cascade,
    // so the FLIP transitions need a JS-side check to honour the OS preference.
    const prefersReducedMotion = usePreferredReducedMotion();
    const pipDimensions = ref({ width: 0, height: 0 });
    let flipAnimation = null;
    // Cancellation token so a superseding watcher run aborts the prior chain's queued el.animate(...).
    let flipRunId = 0;
    watch(isPip, (active) => {
        flipRunId++;
        const myRunId = flipRunId;
        const el = resolveTargetEl();
        if (!el)
            return;
        if (!active) {
            flipAnimation?.cancel();
            flipAnimation = null;
            // Reverse FLIP only when scrolling back into view (not on dismiss/pause)
            if (isScrolledOut.value)
                return;
            if (typeof el.animate !== "function")
                return;
            if (prefersReducedMotion.value === "reduce")
                return;
            const pipRect = el.getBoundingClientRect();
            afterRenderFlush(() => {
                if (myRunId !== flipRunId)
                    return;
                const inlineRect = el.getBoundingClientRect();
                if (inlineRect.width === 0)
                    return;
                const dx = pipRect.left - inlineRect.left;
                const dy = pipRect.top - inlineRect.top;
                const sx = pipRect.width / inlineRect.width;
                const sy = pipRect.height / inlineRect.height;
                flipAnimation = el.animate([
                    { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: "0 0" },
                    { transform: "none", transformOrigin: "0 0" }
                ], { duration: 300, easing: "ease-out" });
            });
            return;
        }
        // Cancel any in-flight FLIP first; its composed transform would skew the captured origin rect.
        flipAnimation?.cancel();
        flipAnimation = null;
        // FLIP: capture origin rect before DOM update (sync flush)
        const originRect = el.getBoundingClientRect();
        pipDimensions.value = { width: originRect.width, height: originRect.height };
        if (typeof el.animate !== "function")
            return;
        if (prefersReducedMotion.value === "reduce")
            return;
        afterRenderFlush(() => {
            if (myRunId !== flipRunId)
                return;
            const finalRect = el.getBoundingClientRect();
            if (finalRect.width === 0)
                return;
            // Land on the resting offset, not "none": the element's inline style is
            // pipStyle = translate(offset), so ending at "none" makes the FLIP settle at
            // the default anchor and snap to the offset once the animation clears (flicker).
            const restX = pipOffset.value.x;
            const restY = pipOffset.value.y;
            const sx = originRect.width / finalRect.width;
            const sy = originRect.height / finalRect.height;
            const dx = originRect.left - finalRect.left + restX;
            // Clamp start Y to viewport top so the animation is visible
            // (the element is above the viewport when PiP activates)
            const dy = Math.max(originRect.top, 0) - finalRect.top + restY;
            flipAnimation = el.animate([
                { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: "0 0" },
                { transform: `translate(${restX}px, ${restY}px)`, transformOrigin: "0 0" }
            ], { duration: 300, easing: "ease-out" });
        });
    }, { flush: "sync" });
    // Cancel any in-flight FLIP if the scope tears down mid-animation (300 ms easing window).
    onScopeDispose(() => {
        flipAnimation?.cancel();
        flipAnimation = null;
    });
    const placeholderStyle = computed(() => {
        if (!isPip.value)
            return undefined;
        return {
            width: `${pipDimensions.value.width}px`,
            height: `${pipDimensions.value.height}px`
        };
    });
    return { placeholderStyle };
};
/**
 * Resting placement of the floating player: panel-collision avoidance, anchor toggle, bounds
 * tracking, bottom-lock, and re-clamp on re-entry. Owns the container inline style (transform + vars).
 */
const usePipPlacement = ({ resolveTargetEl, isPip, pipOffset, clampOffset, clearances }) => {
    // Collision-avoidance shift (px) toward inline-start, applied only as far as
    // needed when an inline-end side panel would overlap the player. Surfaced as
    // --rec-pip-shift so the CSS inset transition animates it.
    const collisionShift = ref(0);
    // Set once the user drags the player while a panel-driven shift is active; tells
    // the close handler to bake the shift in place rather than animate a restore.
    let draggedSincePanelShift = false;
    // Suppresses the inset transition for one tick while baking the shift into the
    // drag offset, so the swap is instantaneous and the player doesn't drift.
    const suppressInsetMotion = ref(false);
    // Resolved `bottom` captured when a panel opens, pinning the player's Y through
    // the Ask AI beacon hiding (which collapses its clearance var and would otherwise
    // drop the player). Released on PiP exit. End-anchored desktop only.
    const bottomLock = ref(null);
    // Horizontal anchor for the PiP container. Vertical anchor is media-query
    // driven (mobile vs desktop) so it stays a pure-CSS concern.
    const pipHorizontalAnchor = ref("end");
    const pipStyle = computed(() => {
        if (!isPip.value)
            return undefined;
        const style = { transform: `translate(${pipOffset.value.x}px, ${pipOffset.value.y}px)` };
        // Only emit the shift when non-zero; otherwise var() resolves to the registered 0 default.
        if (collisionShift.value > 0)
            style["--rec-pip-shift"] = `${collisionShift.value}px`;
        if (bottomLock.value)
            style["--rec-pip-bottom-lock"] = bottomLock.value;
        return style;
    });
    // Reactive rendered bounds of the floating player. Rect-based so the side label tracks
    // drag + collision shift, not just the logical anchor. Refreshed at each settle point.
    const { left: pipLeft, right: pipRight, update: updatePipBounds } = useElementBounding(() => resolveTargetEl(), 
    // position:fixed — page scroll doesn't move it; defer the first read to the settle points below.
    { windowScroll: false, immediate: false });
    const { width: viewportWidth } = useWindowSize();
    // Physical side the player currently rests on (RTL falls out — measures real px).
    const pipPhysicalSide = computed(() => viewportWidth.value - pipRight.value > pipLeft.value ? "left" : "right");
    // Re-measure once the inset collision transition settles; target guard skips bubbling VJS transitions.
    useEventListener(() => resolveTargetEl(), "transitionend", (event) => {
        if (event.target === resolveTargetEl())
            updatePipBounds();
    });
    // Keep both inline edges in the viewport for the current shift, derived from inset + width
    // (transition-invariant) so a mid-transition shift change still clamps right. End-anchored only.
    const clampShiftedOffsetX = () => {
        const el = resolveTargetEl();
        if (!el || typeof window === "undefined")
            return;
        const { width } = el.getBoundingClientRect();
        if (width === 0)
            return;
        const isRTL = isRtl();
        const insetEnd = PIP_BASE_INSET_PX + collisionShift.value;
        const upper = isRTL ? window.innerWidth - insetEnd - width : insetEnd;
        const lower = isRTL ? -insetEnd : insetEnd + width - window.innerWidth;
        const clampedX = Math.min(upper, Math.max(lower, pipOffset.value.x));
        if (clampedX !== pipOffset.value.x)
            pipOffset.value = { x: clampedX, y: pipOffset.value.y };
    };
    // The PiP only avoids the panel while it's floating, end-anchored, and the viewport
    // is wide enough that the panel isn't ~full-width (below that the PiP is top-anchored).
    const isEndAnchoredDesktopPip = (el) => isPip.value &&
        !!el &&
        typeof window !== "undefined" &&
        pipHorizontalAnchor.value === "end" &&
        window.innerWidth >= PIP_COLLISION_MIN_VIEWPORT_PX;
    // Shift toward inline-start just enough to clear the open panel; edges from inset + offset + width
    // (transition-invariant). Yields to the viewport so a narrow viewport parks it under the panel, not off-screen.
    const recomputeCollisionShift = () => {
        const el = resolveTargetEl();
        const endAnchoredDesktopPip = isEndAnchoredDesktopPip(el);
        const panelExtent = toValue(clearances)?.end ?? 0;
        const previousShift = collisionShift.value;
        if (endAnchoredDesktopPip && el && panelExtent > 0) {
            const isRTL = isRtl();
            const offsetX = pipOffset.value.x;
            const { width } = el.getBoundingClientRect();
            const baseEndDist = PIP_BASE_INSET_PX + (isRTL ? offsetX : -offsetX);
            const startRoom = window.innerWidth - PIP_BASE_INSET_PX - width + (isRTL ? -offsetX : offsetX);
            const required = Math.max(0, panelExtent + PIP_PANEL_GAP_PX - baseEndDist);
            collisionShift.value = Math.min(required, Math.max(0, startRoom));
        }
        else {
            collisionShift.value = 0;
        }
        // Re-bound the offset only when the shift changed, so opening/closing can't push it off-screen.
        if (endAnchoredDesktopPip && collisionShift.value !== previousShift)
            clampShiftedOffsetX();
    };
    // Single-pointer alternative to dragging (WCAG 2.5.7): move the player to the side the
    // tooltip promises — the opposite of where it physically sits now (drag included) — and
    // reset the drag offset so it lands cleanly on that side's anchor. Mobile hides the
    // button via CSS so an accidental swipe can't lose the player.
    const togglePipAnchor = () => {
        // The right side is the `end` anchor in LTR and the `start` anchor in RTL.
        const goingRight = pipPhysicalSide.value === "left";
        const rtl = isRtl();
        pipHorizontalAnchor.value = goingRight === !rtl ? "end" : "start";
        pipOffset.value = { x: 0, y: 0 };
        // Re-apply panel avoidance for the new resting spot (the anchor watch only fires on a change).
        recomputeCollisionShift();
        // Instant inset swap (no transition) — refresh bounds after the DOM settles.
        void nextTick(updatePipBounds);
    };
    // Pin the player's resolved bottom on panel open, before the beacon's clearance var
    // collapses; only the end-anchored desktop bottom rule reads the lock.
    const captureBottomLock = () => {
        const el = resolveTargetEl();
        if (!el || typeof window === "undefined")
            return;
        if (!isEndAnchoredDesktopPip(el))
            return;
        if (bottomLock.value !== null)
            return;
        bottomLock.value = window.getComputedStyle(el).bottom;
    };
    // Fold the active shift into the drag offset so dropping the shift leaves the player
    // visually put. The inset transition is suppressed for the swap so it's instantaneous.
    const bakeCollisionShiftIntoOffset = () => {
        const isRTL = isRtl();
        suppressInsetMotion.value = true;
        pipOffset.value = {
            x: pipOffset.value.x + (isRTL ? collisionShift.value : -collisionShift.value),
            y: pipOffset.value.y
        };
        collisionShift.value = 0;
        void nextTick(() => {
            // Force a reflow so the browser commits the shift-removal while transitions are
            // off; without this the no-motion class is toggled back within the same paint and
            // the inset animates (player jumps via transform, then slides via inset).
            resolveTargetEl()?.getBoundingClientRect();
            suppressInsetMotion.value = false;
        });
    };
    const markDraggedWhileShifted = () => {
        // A drag taken while a panel is pushing the player means the user has claimed
        // manual control; closing the panel must then leave the player where it is.
        if (collisionShift.value !== 0)
            draggedSincePanelShift = true;
    };
    // Re-clamp if the viewport shrinks under the current position; the panel's
    // inline-end edge also tracks the viewport, so re-check collision too.
    const handleResize = () => {
        if (!isPip.value)
            return;
        pipOffset.value = clampOffset(pipOffset.value.x, pipOffset.value.y);
        recomputeCollisionShift();
    };
    useEventListener(window, "resize", handleResize);
    // React to the panel opening / closing / resizing. Sync so the open branch captures
    // the Y-lock before the beacon hides and its clearance var collapses.
    watch(() => toValue(clearances)?.end ?? 0, (extent, previousExtent) => {
        const wasOpen = (previousExtent ?? 0) > 0;
        const isOpen = extent > 0;
        if (!wasOpen && isOpen) {
            // Forget any prior drag, pin Y, then push the player off the panel.
            draggedSincePanelShift = false;
            captureBottomLock();
            recomputeCollisionShift();
        }
        else if (wasOpen && !isOpen) {
            // Close: a player dragged while shifted stays put (bake); otherwise it restores.
            if (draggedSincePanelShift && collisionShift.value !== 0) {
                bakeCollisionShiftIntoOffset();
            }
            else {
                recomputeCollisionShift();
            }
        }
        else {
            // Resize while open.
            recomputeCollisionShift();
        }
    }, { flush: "sync" });
    watch(pipHorizontalAnchor, () => recomputeCollisionShift(), { flush: "post" });
    // Re-clamp on PiP re-entry so a persisted offset that no longer fits the
    // viewport (resize while inline) snaps back inside before the user sees it.
    // post-flush so clampOffset measures the PiP-anchored rect, not the inline one.
    // Skip when offset is zero — the anchored baseline is always in-bounds, and
    // reading the rect here would consume getBoundingClientRect mocks the FLIP
    // animation watcher also relies on.
    watch(isPip, (active) => {
        // A newly-floating player may already sit under an open panel; an exiting one clears its shift.
        recomputeCollisionShift();
        if (!active) {
            // Re-track the beacon's clearance on the next PiP session.
            bottomLock.value = null;
            return;
        }
        // Capture the resting bounds for the side label before the FLIP overlay runs.
        updatePipBounds();
        if (pipOffset.value.x === 0 && pipOffset.value.y === 0)
            return;
        pipOffset.value = clampOffset(pipOffset.value.x, pipOffset.value.y);
    }, { flush: "post" });
    return {
        pipStyle,
        pipHorizontalAnchor,
        pipPhysicalSide,
        togglePipAnchor,
        suppressInsetMotion,
        updatePipBounds,
        markDraggedWhileShifted
    };
};
/**
 * Pointer-drag repositioning plus a capture-phase click gate: a pointerdown becomes a drag only
 * past DRAG_THRESHOLD_PX, so plain clicks still reach VideoJS while the gate kills the post-drag toggle.
 */
const usePipDrag = ({ resolveTargetEl, isPip, pipOffset, clampOffset, markDraggedWhileShifted, updatePipBounds }) => {
    const isDragging = ref(false);
    let dragPointerStart = null;
    let dragOffsetStart = { x: 0, y: 0 };
    let dragPassedThreshold = false;
    let dragEndedAt = 0;
    const onPipClick = (e) => {
        if (performance.now() - dragEndedAt < POST_DRAG_CLICK_GATE_MS) {
            e.stopImmediatePropagation();
            e.preventDefault();
        }
    };
    const onDragMove = (e) => {
        if (!dragPointerStart || e.pointerId !== dragPointerStart.pointerId)
            return;
        const dx = e.clientX - dragPointerStart.x;
        const dy = e.clientY - dragPointerStart.y;
        if (!dragPassedThreshold) {
            if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX)
                return;
            dragPassedThreshold = true;
            isDragging.value = true;
            markDraggedWhileShifted();
            // Capture only once a drag is confirmed, so a plain click still reaches VideoJS.
            const el = resolveTargetEl();
            if (el && typeof el.setPointerCapture === "function") {
                try {
                    el.setPointerCapture(e.pointerId);
                }
                catch {
                    /* pointer already released */
                }
            }
        }
        pipOffset.value = clampOffset(dragOffsetStart.x + dx, dragOffsetStart.y + dy);
    };
    // Single source of truth so onDragEnd and onScopeDispose can't drift apart.
    const stopDragPointerListeners = () => {
        if (typeof window === "undefined")
            return;
        window.removeEventListener("pointermove", onDragMove);
        window.removeEventListener("pointerup", onDragEnd);
        window.removeEventListener("pointercancel", onDragEnd);
    };
    const onDragEnd = (e) => {
        if (!dragPointerStart || e.pointerId !== dragPointerStart.pointerId)
            return;
        stopDragPointerListeners();
        const wasDrag = dragPassedThreshold;
        if (wasDrag) {
            dragEndedAt = performance.now();
            e.preventDefault();
        }
        dragPointerStart = null;
        dragPassedThreshold = false;
        isDragging.value = false;
        // A real drag moved the player — refresh the rendered bounds for the side label.
        if (wasDrag)
            updatePipBounds();
    };
    const onPipPointerDown = (e) => {
        if (!isPip.value)
            return;
        if (e.button !== 0)
            return;
        if (e.target instanceof Element && e.target.closest(DRAG_IGNORE_SELECTOR))
            return;
        dragPointerStart = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
        dragOffsetStart = { x: pipOffset.value.x, y: pipOffset.value.y };
        dragPassedThreshold = false;
        window.addEventListener("pointermove", onDragMove);
        window.addEventListener("pointerup", onDragEnd);
        window.addEventListener("pointercancel", onDragEnd);
    };
    // Capture-phase click gate on the PiP container; preempts VideoJS's bubble-phase listener on the inner <video>.
    let clickGateEl = null;
    const attachClickGate = (el) => {
        if (clickGateEl === el)
            return;
        clickGateEl?.removeEventListener("click", onPipClick, true);
        clickGateEl = el;
        el?.addEventListener("click", onPipClick, { capture: true });
    };
    watch(() => resolveTargetEl(), (el) => attachClickGate(el), { immediate: true });
    onScopeDispose(() => {
        stopDragPointerListeners();
        attachClickGate(null);
    });
    return { isDragging, onPipPointerDown, onPipClick };
};
export const useVideoPip = (targetRef, isPlaying, options = {}) => {
    const { threshold = 0.66 } = options;
    const { isPip, isScrolledOut, dismiss } = usePipVisibility({
        targetRef,
        isPlaying,
        threshold,
        enabled: options.enabled
    });
    const { showPipExitAnnouncement } = usePipAnnouncement(isPip);
    // Drag offset persists across PiP exit/re-entry within the page session so a
    // user-positioned PiP doesn't snap back every time the inline player scrolls
    // through the viewport. Resets only on full page reload.
    const pipOffset = ref({ x: 0, y: 0 });
    const resolveTargetEl = () => options.contentRef ? toValue(options.contentRef) : toValue(targetRef);
    // Clamp so the entire container stays inside the viewport. The element's
    // current rect already includes any active offset, so we subtract it to
    // recover the anchored (no-transform) rect.
    const clampOffset = (rawX, rawY) => {
        const el = resolveTargetEl();
        if (!el || typeof window === "undefined")
            return { x: rawX, y: rawY };
        const rect = el.getBoundingClientRect();
        const baseLeft = rect.left - pipOffset.value.x;
        const baseTop = rect.top - pipOffset.value.y;
        return {
            x: Math.max(-baseLeft, Math.min(window.innerWidth - baseLeft - rect.width, rawX)),
            y: Math.max(-baseTop, Math.min(window.innerHeight - baseTop - rect.height, rawY))
        };
    };
    const { placeholderStyle } = usePipFlip({ resolveTargetEl, isPip, isScrolledOut, pipOffset });
    const { pipStyle, pipHorizontalAnchor, pipPhysicalSide, togglePipAnchor, suppressInsetMotion, updatePipBounds, markDraggedWhileShifted } = usePipPlacement({ resolveTargetEl, isPip, pipOffset, clampOffset, clearances: options.clearances });
    const { isDragging, onPipPointerDown, onPipClick } = usePipDrag({
        resolveTargetEl,
        isPip,
        pipOffset,
        clampOffset,
        markDraggedWhileShifted,
        updatePipBounds
    });
    return {
        isPip: readonly(isPip),
        showPipExitAnnouncement: readonly(showPipExitAnnouncement),
        dismiss,
        placeholderStyle,
        pipStyle,
        pipHorizontalAnchor: readonly(pipHorizontalAnchor),
        pipPhysicalSide,
        togglePipAnchor,
        isDragging: readonly(isDragging),
        suppressInsetMotion: readonly(suppressInsetMotion),
        onPipPointerDown,
        onPipClick
    };
};
